import type { EChartsOption, MarkAreaComponentOption, MarkLineComponentOption, SeriesOption } from 'echarts'
import { buildChartGrid, buildDataZoom, buildMultiSeriesTooltipFormatter, buildTimeXAxis, buildValueYAxis, type ChartDataZoom } from './base-chart-options'

export interface VoltagePrecariousSetpoint {
  value: number
  label: string
}

type VoltageSeriesKey = 'voltage_a' | 'voltage_b' | 'voltage_c' | 'voltage_ab' | 'voltage_bc' | 'voltage_ca'

export interface VoltageChartDataParams {
  seriesVoltageA: [number, number][]
  seriesVoltageB: [number, number][]
  seriesVoltageC: [number, number][]
  seriesVoltageAb?: [number, number][]
  seriesVoltageBc?: [number, number][]
  seriesVoltageCa?: [number, number][]
  fixedVoltages: Record<string, number>
    phaseNames: [string, string, string] | null
  phasePhaseNames?: [string, string, string] | null
  precariousSetpoints?: VoltagePrecariousSetpoint[]
}

export interface BuildVoltageSeriesParams extends VoltageChartDataParams {
  alarmMarkArea?: MarkAreaComponentOption
  hoveredMarkArea?: MarkAreaComponentOption
}

const VOLTAGE_PHASE_COLORS: Record<'voltage_a' | 'voltage_b' | 'voltage_c', string> = {
  voltage_a: '#a500a5',
  voltage_b: '#ff7e27',
  voltage_c: '#31c0f6',
}

const VOLTAGE_LINE_PHASE_COLORS: Record<'voltage_ab' | 'voltage_bc' | 'voltage_ca', string> = {
  voltage_ab: '#2563eb',
  voltage_bc: '#16a34a',
  voltage_ca: '#db2777',
}

const VOLTAGE_SETPOINT_COLOR = '#dc2626'

interface VoltageSeriesDefinition {
  key: VoltageSeriesKey
  name: string
  data: [number, number][]
  color: string
}

function buildVoltageSeriesDefinitions(params: BuildVoltageSeriesParams): VoltageSeriesDefinition[] {
  const definitions: VoltageSeriesDefinition[] = []

  if (params.phaseNames) {
    definitions.push(
      { key: 'voltage_a', name: params.phaseNames[0], data: params.seriesVoltageA, color: VOLTAGE_PHASE_COLORS.voltage_a },
      { key: 'voltage_b', name: params.phaseNames[1], data: params.seriesVoltageB, color: VOLTAGE_PHASE_COLORS.voltage_b },
      { key: 'voltage_c', name: params.phaseNames[2], data: params.seriesVoltageC, color: VOLTAGE_PHASE_COLORS.voltage_c },
    )
  }

  if (params.phasePhaseNames) {
    definitions.push(
      { key: 'voltage_ab', name: params.phasePhaseNames[0], data: params.seriesVoltageAb ?? [], color: VOLTAGE_LINE_PHASE_COLORS.voltage_ab },
      { key: 'voltage_bc', name: params.phasePhaseNames[1], data: params.seriesVoltageBc ?? [], color: VOLTAGE_LINE_PHASE_COLORS.voltage_bc },
      { key: 'voltage_ca', name: params.phasePhaseNames[2], data: params.seriesVoltageCa ?? [], color: VOLTAGE_LINE_PHASE_COLORS.voltage_ca },
    )
  }

  return definitions
}

export function buildVoltageChartSeries(params: BuildVoltageSeriesParams): SeriesOption[] {
  const { fixedVoltages, alarmMarkArea, hoveredMarkArea, precariousSetpoints } = params

  return buildVoltageSeriesDefinitions(params).map(({ key, name, data, color }, index) => {
    const fixedValue = fixedVoltages[key]

    const markLineData: NonNullable<MarkLineComponentOption['data']> = []
    if (typeof fixedValue === 'number') {
      markLineData.push({
        yAxis: fixedValue,
        lineStyle: { type: 'dashed', color, width: 1.5 },
        label: { formatter: `${fixedValue} V` },
      })
    }
    if (index === 0) {
      for (const setpoint of precariousSetpoints ?? []) {
        markLineData.push({
          yAxis: setpoint.value,
          lineStyle: { type: 'dashed', color: VOLTAGE_SETPOINT_COLOR, width: 1.5 },
          label: { formatter: setpoint.label },
        })
      }
    }

    return {
      name,
      type: 'line',
      smooth: false,
      connectNulls: true,
      data,
      symbol: 'none',
      lineStyle: { width: 2, color },
      itemStyle: { color },
      markLine:
        markLineData.length > 0
          ? {
              silent: true,
              symbol: 'none',
              lineStyle: { type: 'dashed', color, width: 1.5 },
              label: {
                show: true,
                position: 'insideEndTop',
                fontSize: 10,
              },
              data: markLineData,
            }
          : undefined,
      markArea: alarmMarkArea ?? (index === 0 ? hoveredMarkArea : undefined),
    }
  })
}

export interface ComputeVoltageYRangeParams {
  series: Array<[number, number][] | undefined>
  fixedVoltages: Record<string, number>
  precariousSetpoints?: VoltagePrecariousSetpoint[]
}

/** Faixa do eixo Y focada nos valores reais de tensão (não começar em 0). */
export function computeVoltageYRange(params: ComputeVoltageYRangeParams): { min: number; max: number } | null {
  const values: number[] = []
  for (const series of params.series) {
    for (const [, v] of series ?? []) {
      if (typeof v === 'number' && !Number.isNaN(v)) values.push(v)
    }
  }
  for (const v of Object.values(params.fixedVoltages)) {
    if (typeof v === 'number' && !Number.isNaN(v)) values.push(v)
  }
  for (const setpoint of params.precariousSetpoints ?? []) {
    if (typeof setpoint.value === 'number' && !Number.isNaN(setpoint.value)) values.push(setpoint.value)
  }
  if (values.length === 0) return null
  const min = Math.min(...values)
  const max = Math.max(...values)
  const padding = Math.max(max - min, 20) * 0.15
  return { min: Math.floor(min - padding), max: Math.ceil(max + padding) }
}

export interface BuildVoltageChartOptionParams extends BuildVoltageSeriesParams {
  dataZoom: ChartDataZoom
  sliderBottom?: number
  showMoveHandle?: boolean
  accentColor?: string
  fillerColor?: string
}

/** Opções ECharts para gráfico de tensão (fases F-N/F-F + tensões fixas + setpoints). */
export function buildVoltageChartOption(params: BuildVoltageChartOptionParams): EChartsOption {
  const { dataZoom, sliderBottom, showMoveHandle, accentColor = '#a500a5', fillerColor = 'rgba(165, 0, 165, 0.2)' } = params

  const series = buildVoltageChartSeries(params)
  const definitions = buildVoltageSeriesDefinitions(params)

  const yRange = computeVoltageYRange({
    series: definitions.map((definition) => definition.data),
    fixedVoltages: params.fixedVoltages,
    precariousSetpoints: params.precariousSetpoints,
  })

  const legendData = definitions.map((definition) => definition.name)

  return {
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'cross' },
      formatter: buildMultiSeriesTooltipFormatter('V', 2),
    },
    legend: {
      data: legendData,
      top: 0,
      textStyle: { fontSize: 10 },
    },
    grid: buildChartGrid(),
    dataZoom: buildDataZoom({ dataZoom, accentColor, fillerColor, sliderBottom, showMoveHandle }),
    xAxis: buildTimeXAxis(),
    yAxis: buildValueYAxis({ unit: 'V', min: yRange?.min, max: yRange?.max }),
    series,
  }
}
